import { useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import axios from "axios";
import { resetReduxState } from "../redux/store";

const WARN_BEFORE_MS = 15 * 60 * 1000; // show warning 15 minutes before expiry

export function expireSession() {
    const cookieDomain = process.env.REACT_APP_COOKIE_DOMAIN;
    const cookieAttrs = { path: "/" };
    if (cookieDomain) cookieAttrs.domain = cookieDomain;

    Cookies.remove("authToken", cookieAttrs);
    Cookies.remove("userData", cookieAttrs);
    Cookies.remove("officeCode", cookieAttrs);
    Cookies.remove("officeCodes", cookieAttrs);

    resetReduxState();
    localStorage.removeItem("isCollapsed");
    localStorage.removeItem("allowedMenus");
    localStorage.removeItem("userPrivileges");
    localStorage.removeItem("menuTabStructure");
    localStorage.removeItem("organizationData");
    localStorage.removeItem("officeCode");
    localStorage.removeItem("organizationTypes");
    localStorage.removeItem("sessionExpiry");
    sessionStorage.clear();

    if (axios.defaults.headers.common.Authorization) {
        delete axios.defaults.headers.common.Authorization;
    }

    window.location.replace("/login");
}

export function useSessionExpiry() {
    const [showWarning, setShowWarning] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);
    // Tracks whether we've already raised the warning for this session window,
    // so we don't re-open the modal after the user extends their session.
    const warningRaisedRef = useRef(false);

    useEffect(() => {
        // Axios interceptor: redirect on 401 Unauthorized (activates once backend enforces JWT)
        const interceptorId = axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    expireSession();
                }
                return Promise.reject(error);
            }
        );

        // Poll every second to keep the countdown accurate and catch expiry promptly.
        const interval = setInterval(() => {
            const sessionExpiry = parseInt(localStorage.getItem("sessionExpiry") || "0", 10);
            if (!sessionExpiry) return;

            const remaining = sessionExpiry - Date.now();

            if (remaining <= 0) {
                expireSession();
                return;
            }

            setSecondsLeft(Math.floor(remaining / 1000));

            if (remaining <= WARN_BEFORE_MS && !warningRaisedRef.current) {
                warningRaisedRef.current = true;
                setShowWarning(true);
            }
        }, 1000);

        // Also check immediately on mount in case it's already expired or within the warning window
        const sessionExpiry = parseInt(localStorage.getItem("sessionExpiry") || "0", 10);
        if (sessionExpiry) {
            const remaining = sessionExpiry - Date.now();
            if (remaining <= 0) {
                expireSession();
                return;
            }
            setSecondsLeft(Math.floor(remaining / 1000));
            if (remaining <= WARN_BEFORE_MS) {
                warningRaisedRef.current = true;
                setShowWarning(true);
            }
        }

        return () => {
            clearInterval(interval);
            axios.interceptors.response.eject(interceptorId);
        };
    }, []);

    const handleExtendSession = async () => {
        setIsRefreshing(true);
        try {
            const token = Cookies.get("authToken");
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/auth/user/refresh-token`,
                null,
                { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
            );

            // Backend returns the new token in the "authToken" response header as "Bearer <token>"
            const newTokenHeader = response.headers["authtoken"];
            if (newTokenHeader) {
                const newToken = newTokenHeader.replace(/^Bearer\s+/i, "");

                const cookieDomain = process.env.REACT_APP_COOKIE_DOMAIN;
                Cookies.set("authToken", newToken, {
                    domain: cookieDomain,
                    expires: 1,
                    secure: process.env.REACT_APP_COOKIE_SECURE === "true",
                    sameSite: "Lax",
                });
                axios.defaults.headers.common.Authorization = `Bearer ${newToken}`;

                const payload = JSON.parse(atob(newToken.split(".")[1]));
                if (payload.exp) {
                    localStorage.setItem("sessionExpiry", (payload.exp * 1000).toString());
                    setSecondsLeft(Math.floor((payload.exp * 1000 - Date.now()) / 1000));
                }
            }

            // Reset so the warning can fire again if the new session also nears expiry
            warningRaisedRef.current = false;
            setShowWarning(false);
        } catch (err) {
            console.error("Session refresh failed:", err);
        } finally {
            setIsRefreshing(false);
        }
    };

    const formatCountdown = (secs) => {
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return `${m}:${s.toString().padStart(2, "0")}`;
    };

    return { showWarning, secondsLeft, isRefreshing, handleExtendSession, formatCountdown };
}
